// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from 'firebase/firestore';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { db } from '../../utils/firebase';
import { CRAWL_DISPATCHER_RUNS_COLLECTION } from '../../utils/firestoreCollections';
import { toDateMaybe, toNumberOrZero } from '../../utils/firestoreFieldConversions';

const PAGE_SIZE = 24;

function dispatcherLagMeanMinutesFromDoc(docData) {
  const lagMsSumTotal = toNumberOrZero(docData.lagMsSum);
  const lagSampleCountTotal = toNumberOrZero(docData.lagMsCount);
  if (lagSampleCountTotal <= 0) {
    return null;
  }
  return Math.round(lagMsSumTotal / lagSampleCountTotal / (60 * 1000));
}

function dispatcherLagMaxMinutesFromDoc(docData) {
  if (typeof docData?.lagMsMax !== 'number' || !Number.isFinite(docData.lagMsMax)) {
    return null;
  }
  return Math.round(docData.lagMsMax / (60 * 1000));
}

function mapDispatcherRunDoc(docSnapshot) {
  const docData = docSnapshot.data();
  const lagMeanMinutes = dispatcherLagMeanMinutesFromDoc(docData);
  const lagMaxMinutes = dispatcherLagMaxMinutesFromDoc(docData);
  const lagSamples = toNumberOrZero(docData.lagMsCount);
  return {
    id: docSnapshot.id,
    startedAt: toDateMaybe(docData.startedAt),
    finishedAt: toDateMaybe(docData.finishedAt),
    dueQueriedCount: toNumberOrZero(docData.dueQueriedCount),
    dispatchedCount: toNumberOrZero(docData.dispatchedCount),
    lagMeanMinutes,
    lagMaxMinutes,
    lagSamples,
    enqueueErrors: toNumberOrZero(docData.enqueueErrors),
    skippedLeaseFailed: toNumberOrZero(docData.skippedLeaseFailed),
    skippedLocked: toNumberOrZero(docData.skippedLocked),
  };
}

function formatTs(value) {
  if (!value) return '—';
  return value instanceof Date ? value.toLocaleString() : String(value);
}

function DispatcherTableRow({ row }) {
  const meanLagHuman = typeof row.lagMeanMinutes === 'number' ? `${row.lagMeanMinutes}m` : '—';
  const maxLagHuman = typeof row.lagMaxMinutes === 'number' ? `${row.lagMaxMinutes}m` : '—';
  return (
    <TableRow>
      <TableCell>{formatTs(row.startedAt)}</TableCell>
      <TableCell>{formatTs(row.finishedAt)}</TableCell>
      <TableCell>{row.dueQueriedCount}</TableCell>
      <TableCell>{row.dispatchedCount}</TableCell>
      <TableCell>{meanLagHuman}</TableCell>
      <TableCell>{maxLagHuman}</TableCell>
      <TableCell>{row.lagSamples}</TableCell>
      <TableCell>{row.enqueueErrors}</TableCell>
      <TableCell>{row.skippedLeaseFailed}</TableCell>
      <TableCell>{row.skippedLocked}</TableCell>
    </TableRow>
  );
}

function DispatcherTableColumnHeaders() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Started</TableCell>
        <TableCell>Finished</TableCell>
        <TableCell>Due queried</TableCell>
        <TableCell>Dispatched</TableCell>
        <TableCell>Mean lag (est.)</TableCell>
        <TableCell>Max lag (est.)</TableCell>
        <TableCell>Lag samples</TableCell>
        <TableCell>Enqueue errors</TableCell>
        <TableCell>Skipped (lease failed)</TableCell>
        <TableCell>Skipped (locked)</TableCell>
      </TableRow>
    </TableHead>
  );
}

function DispatcherTableBody({ rows }) {
  if (rows.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={10}>
            <Typography variant="body2" color="text.secondary">
              No dispatcher runs yet.
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }
  return (
    <TableBody>
      {rows.map(function dispatcherRunRow(dispatcherRow) {
        return <DispatcherTableRow key={dispatcherRow.id} row={dispatcherRow} />;
      })}
    </TableBody>
  );
}

function DispatcherRunsTable({ rows }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
      <Table size="small" sx={{ width: '100%' }}>
        <DispatcherTableColumnHeaders />
        <DispatcherTableBody rows={rows} />
      </Table>
    </TableContainer>
  );
}

function DispatcherPagerBar({ page, rowsLength, hasNext, onPrevious, onNext }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
      <Typography variant="body2" color="text.secondary">
        Page {page + 1}
        {rowsLength ? ` · ${rowsLength} row(s)` : ''}
      </Typography>
      <Box display="flex" gap={1}>
        <Button variant="outlined" disabled={page === 0} onClick={onPrevious}>
          Previous
        </Button>
        <Button variant="outlined" disabled={!hasNext} onClick={onNext}>
          Next
        </Button>
      </Box>
    </Box>
  );
}

function DispatcherTab() {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const lastDocPerPage = useRef([]);
  const latestLoadIdRef = useRef(0);

  const loadPage = useCallback(async (targetPage) => {
    const loadId = ++latestLoadIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const runsCollection = collection(db, CRAWL_DISPATCHER_RUNS_COLLECTION);
      if (targetPage === 0) {
        lastDocPerPage.current = [];
      }
      const previousLastDoc = targetPage === 0 ? null : lastDocPerPage.current[targetPage - 1];
      if (targetPage > 0 && !previousLastDoc) {
        return;
      }
      const listQuery =
        targetPage === 0
          ? query(
              runsCollection,
              orderBy('startedAt', 'desc'),
              limit(PAGE_SIZE + 1),
            )
          : query(
              runsCollection,
              orderBy('startedAt', 'desc'),
              startAfter(previousLastDoc),
              limit(PAGE_SIZE + 1),
            );
      const snap = await getDocs(listQuery);
      if (loadId !== latestLoadIdRef.current) {
        return;
      }
      const docs = snap.docs;
      const more = docs.length > PAGE_SIZE;
      const pageDocs = more ? docs.slice(0, PAGE_SIZE) : docs;
      const mapped = pageDocs.map(mapDispatcherRunDoc);
      if (pageDocs.length > 0) {
        lastDocPerPage.current[targetPage] = pageDocs[pageDocs.length - 1];
        lastDocPerPage.current.length = targetPage + 1;
      } else {
        lastDocPerPage.current[targetPage] = null;
      }
      setRows(mapped);
      setHasNext(more);
      setPage(targetPage);
    } catch (err) {
      if (loadId !== latestLoadIdRef.current) {
        return;
      }
      console.error(err);
      setError(err);
    } finally {
      if (loadId === latestLoadIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  const handlePrevPage = useCallback(() => {
    loadPage(page - 1);
  }, [loadPage, page]);

  const handleNextPage = useCallback(() => {
    loadPage(page + 1);
  }, [loadPage, page]);

  const errorBanner = error ? (
    <Typography color="error" sx={{ mb: 2 }}>
      Unable to load dispatcher runs. You can try again; details are in the browser console.
    </Typography>
  ) : null;

  const mainContent = loading ? (
    <Box display="flex" justifyContent="center" py={4}>
      <CircularProgress />
    </Box>
  ) : error ? null : (
    <>
      <DispatcherRunsTable rows={rows} />
      <DispatcherPagerBar
        page={page}
        rowsLength={rows.length}
        hasNext={hasNext}
        onPrevious={handlePrevPage}
        onNext={handleNextPage}
      />
    </>
  );

  return (
    <Box>
      {errorBanner}
      {mainContent}
    </Box>
  );
}

export default DispatcherTab;
